import { get } from 'lodash';
import i18next from 'i18next';

import importTypeXlsConstants from '@commons/Excels/constants/importType.xslx.constant';
import productsXlsConstants from '@commons/Excels/constants/products.xlsx.constant';

import { normalizeJsonObject, mapColumnIdsAndNormalizedColumnIds } from '@backoffice/utils';
import normalizeStringValue from '@commons/utils/normalizeStringValue';

// This function is used both for batch creation and batch update because the format is the same
export const formatProductBatchUpdateOrCreationFile = (
  products,
  clientChannels = [],
  defaultChannelId,
  { isBatchUpdate = false } = {},
) => {
  const formattedProducts = normalizeJsonObject(products);

  // Sheet configuration
  const sheetConfig = productsXlsConstants.getSheetConfiguration();

  // Categories
  const hiddenSheetCategoryConfig = productsXlsConstants.getHiddenSheetCategoryConfig();

  // SubCategories
  const hiddenSheetSubCategoryConfig = productsXlsConstants.getHiddenSheetSubCategoryConfig();

  // Brands
  const hiddenSheetBrandConfig = productsXlsConstants.getHiddenSheetBrandConfig();

  // Associated Ingredients
  const hiddenSheetAssociatedIngredientConfig =
    productsXlsConstants.getHiddenSheetAssociatedIngredientConfig();

  // Associated Recipes
  const hiddenSheetAssociatedRecipeConfig =
    productsXlsConstants.getHiddenSheetAssociatedRecipeConfig();

  // Channel config
  const nonDefaultChannels = clientChannels.filter(({ id }) => id !== defaultChannelId);

  const channelKeyToId = nonDefaultChannels.reduce((acc, { id, translationKey }) => {
    acc[`${translationKey}_PRICE_WITH_TAXES`] = { channelId: id, field: 'priceWithTaxes' };
    acc[`${translationKey}_VAT_RATE`] = { channelId: id, field: 'vatRate' };
    return acc;
  }, {});

  const normalizedChannelKeyMap = nonDefaultChannels.reduce((acc, { translationKey }) => {
    const channelName = i18next.t(`${translationKey}.SHORTEN`);

    const normalizedPrice = normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.CHANNEL_PRICE_WITH_TAXES', { channelName }),
    );

    const normalizedVat = normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.CHANNEL_VAT_RATE', { channelName }),
    );

    acc[normalizedPrice] = `${translationKey}_PRICE_WITH_TAXES`;
    acc[normalizedVat] = `${translationKey}_VAT_RATE`;

    return acc;
  }, {});

  return Object.keys(formattedProducts).reduce((result, sheetName) => {
    if (!result[importTypeXlsConstants.KEY]) {
      result[importTypeXlsConstants.KEY] = get(formattedProducts, 'ImportType[0].importtype', null);
    }

    if (sheetName === sheetConfig.NAME) {
      result[sheetConfig.ID] = formattedProducts[sheetName].map((current) => {
        const mapped = mapColumnIdsAndNormalizedColumnIds(
          current,
          productsXlsConstants.getNormalizedColumnIds({ isBatchUpdate }),
        );

        const channelPricesMap = {};

        Object.entries(current).forEach(([normalizedKey, value]) => {
          const internalKey = normalizedChannelKeyMap[normalizedKey];
          if (!internalKey) {
            return;
          }

          const { channelId, field } = channelKeyToId[internalKey];
          if (!channelPricesMap[channelId]) {
            channelPricesMap[channelId] = { channelId };
          }
          channelPricesMap[channelId][field] = value;
        });

        return {
          ...mapped,
          channelPrices: Object.values(channelPricesMap), // [] if mono-channel client
        };
      });
    }

    // Category
    if (sheetName === hiddenSheetCategoryConfig.SHEET.NAME) {
      result[hiddenSheetCategoryConfig.SHEET.ID] = formattedProducts[sheetName].map((current) =>
        mapColumnIdsAndNormalizedColumnIds(
          current,
          productsXlsConstants.getHiddenSheetCategoryNormalizedColumnIds(),
        ),
      );
    }

    // Subcategory
    if (sheetName === hiddenSheetSubCategoryConfig.SHEET.NAME) {
      result[hiddenSheetSubCategoryConfig.SHEET.ID] = formattedProducts[sheetName].map((current) =>
        mapColumnIdsAndNormalizedColumnIds(
          current,
          productsXlsConstants.getHiddenSheetSubCategoryNormalizedColumnIds(),
        ),
      );
    }

    // Brand
    if (sheetName === hiddenSheetBrandConfig.SHEET.NAME) {
      result[hiddenSheetBrandConfig.SHEET.ID] = formattedProducts[sheetName].map((current) =>
        mapColumnIdsAndNormalizedColumnIds(
          current,
          productsXlsConstants.getHiddenSheetBrandNormalizedColumnIds(),
        ),
      );
    }

    // Associated ingredient
    if (sheetName === hiddenSheetAssociatedIngredientConfig.SHEET.NAME) {
      result[hiddenSheetAssociatedIngredientConfig.SHEET.ID] = formattedProducts[sheetName].map(
        (current) =>
          mapColumnIdsAndNormalizedColumnIds(
            current,
            productsXlsConstants.getHiddenSheetAssociatedIngredientNormalizedColumnIds(),
          ),
      );
    }

    // Associated recipe
    if (sheetName === hiddenSheetAssociatedRecipeConfig.SHEET.NAME) {
      result[hiddenSheetAssociatedRecipeConfig.SHEET.ID] = formattedProducts[sheetName].map(
        (current) =>
          mapColumnIdsAndNormalizedColumnIds(
            current,
            productsXlsConstants.getHiddenSheetAssociatedRecipeNormalizedColumnIds(),
          ),
      );
    }

    return result;
  }, {});
};

export default { formatProductBatchUpdateOrCreationFile };
